import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { GearSix, UploadSimple, Check } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

const ACCENTS = [
  { name: "Emerald", value: "#10B981" },
  { name: "Electric Blue", value: "#3B82F6" },
  { name: "Violet", value: "#8B5CF6" },
  { name: "Amber", value: "#F59E0B" },
  { name: "Rose", value: "#F43F5E" },
];

export default function Settings() {
  const { isOwner } = useAuth();
  const [s, setS] = useState({ company_name: "", accent_color: "#10B981", logo_path: null, public_base_url: "" });
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  useEffect(() => { api.get("/settings").then((r) => setS(r.data)).catch(() => {}); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", { company_name: s.company_name, accent_color: s.accent_color, public_base_url: (s.public_base_url || "").trim().replace(/\/$/, "") });
      toast.success("Branding saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setS((p) => ({ ...p, logo_path: data.logo_path }));
      toast.success("Logo uploaded");
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
  };

  const logoUrl = s.logo_path ? `${api.defaults.baseURL}/branding/logo?v=${s.logo_path}` : null;

  return (
    <div className="p-5 md:p-8 max-w-2xl mx-auto">
      <div className="overline text-primary">Settings</div>
      <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Branding</h1>
      <p className="text-muted-foreground text-sm mt-1">Brand both the admin console and merchant portals.</p>

      <div className="mt-6 rounded-[var(--radius)] border border-border bg-card p-6 space-y-6">
        <div>
          <Label className="overline text-muted-foreground">Company Name</Label>
          <Input className="bg-secondary border-border mt-1.5 h-11" value={s.company_name} onChange={(e) => setS({ ...s, company_name: e.target.value })} disabled={!isOwner} data-testid="company-name-input" />
        </div>

        <div>
          <Label className="overline text-muted-foreground">Merchant Link Domain</Label>
          <Input className="bg-secondary border-border mt-1.5 h-11 num" value={s.public_base_url || ""} onChange={(e) => setS({ ...s, public_base_url: e.target.value })} disabled={!isOwner} placeholder="https://app.yourcompany.com" data-testid="public-base-url-input" />
          <p className="text-[11px] text-muted-foreground mt-1.5">The base URL used when generating merchant offer links (e.g. https://app.businesslineoc.com). Leave blank to use the current site address. The domain must point to this app for links to open.</p>
        </div>

        <div>
          <Label className="overline text-muted-foreground">Logo</Label>
          <div className="flex items-center gap-4 mt-1.5">
            <div className="h-16 w-16 rounded-xl border border-border bg-secondary flex items-center justify-center overflow-hidden">
              {logoUrl ? <img src={logoUrl} alt="logo" className="h-full w-full object-cover" /> : <GearSix size={22} className="text-muted-foreground" />}
            </div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadLogo} />
            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={!isOwner} data-testid="upload-logo-button">
              <UploadSimple size={16} className="mr-1" /> Upload logo
            </Button>
          </div>
        </div>

        <div>
          <Label className="overline text-muted-foreground">Accent Color</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {ACCENTS.map((a) => (
              <button
                key={a.value}
                onClick={() => isOwner && setS({ ...s, accent_color: a.value })}
                data-testid={`accent-${a.name.toLowerCase().replace(" ", "-")}`}
                className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-sm transition-colors ${s.accent_color === a.value ? "border-foreground/40" : "border-border"}`}
              >
                <span className="h-4 w-4 rounded-full" style={{ background: a.value }} />
                {a.name}
                {s.accent_color === a.value && <Check size={14} weight="bold" />}
              </button>
            ))}
          </div>
        </div>

        {isOwner && <Button onClick={save} disabled={saving} data-testid="save-settings-button" className="font-semibold">{saving ? "Saving…" : "Save branding"}</Button>}
        {!isOwner && <p className="text-xs text-muted-foreground">Only owners can edit branding.</p>}
      </div>
    </div>
  );
}
